import { useRef, useState, useEffect, useContext } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import I18n from "i18n-js";
import {
  AudioConfig,
  ResultReason,
  SpeechConfig,
  SpeechRecognizer,
  SpeechSynthesizer,
} from "microsoft-cognitiveservices-speech-sdk";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import Constants from "common/constant";
import { CloseBtn } from "common/elements";
import { useScale, usePlaySound } from "common/utils";
import { ConfigContext } from "features/top";
import { createChatMessage } from "./api";

export const RobotAssistant = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const [render, setRender] = useState(false);
  const [streaming, _setStreaming] = useState(false);
  const streamingRef = useRef(streaming);
  const setStreaming = (streaming: boolean) => {
    streamingRef.current = streaming;
    _setStreaming(streaming);
  };
  const { audioContext } = useContext(ConfigContext);
  const assistant = useSelector(
    (state: RootState) => state.config.dialog.assistant
  );
  const [source, setSource] = useState<AudioBufferSourceNode | null>(null);
  const [text, setText] = useState(I18n.t("MSG_ASSISTANT_WELCOME_MESSAGE"));
  const [recognizer, setRecognizer] = useState<SpeechRecognizer | null>(null);
  const [synthesizer, setSynthesizer] = useState<SpeechSynthesizer | null>(
    null
  );

  const animationEnd = () => {
    if (!assistant.value) setRender(false);
  };

  const handleAudioPlay = async (
    audioData: ArrayBuffer,
    recognizer: SpeechRecognizer
  ) => {
    if (audioData && audioContext) {
      const decodedData = await audioContext.decodeAudioData(audioData);
      const source = audioContext.createBufferSource();
      source.buffer = decodedData;
      source.connect(audioContext.destination);
      source.start(0);
      source.onended = () => {
        setSource(null);
        recognizer.recognizeOnceAsync();
      };
      setSource(source);
    }
  };

  const handleRecognize = (
    text: string,
    recognizer: SpeechRecognizer,
    synthesizer: SpeechSynthesizer
  ) => {
    console.log(`RECOGNIZED: Text=${text}`);
    createChatMessage({ message: text }).then((response) => {
      let text = "";
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const read = () =>
        reader.read().then(({ done, value }) => {
          if (!streamingRef.current) {
            recognizer.recognizeOnceAsync();
            return;
          }

          if (done) {
            synthesizer.speakTextAsync(text, async (result) => {
              const { audioData } = result;
              handleAudioPlay(audioData, recognizer);
            });
            return;
          }

          let chunk = decoder.decode(value);
          text += chunk;
          setText(text);
          read();
        });
      read();
    });
  };

  useEffect(() => {
    setRender(assistant.value);
    setText(I18n.t("MSG_ASSISTANT_WELCOME_MESSAGE"));
    if (assistant.value) {
      const speechConfig = SpeechConfig.fromSubscription(
        Constants.azureSpeechKey,
        Constants.azureSpeechRegion
      );
      speechConfig.speechRecognitionLanguage = "ja-JP";
      speechConfig.speechSynthesisVoiceName = "ja-JP-NanamiNeural";
      const inputAudioConfig = AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new SpeechRecognizer(speechConfig, inputAudioConfig);
      const synthesizer = new SpeechSynthesizer(speechConfig, null);
      setRecognizer(recognizer);
      setSynthesizer(synthesizer);

      recognizer.recognizing = (s, e) => {
        setText(e.result.text);
      };
      recognizer.recognized = async (s, e) => {
        setStreaming(true);
        if (e.result.reason === ResultReason.RecognizedSpeech) {
          handleRecognize(e.result.text, recognizer, synthesizer);
        } else if (e.result.reason == ResultReason.NoMatch) {
          setText("質問は何かな？話しかけてね！");
          recognizer.recognizeOnceAsync();
        }
      };
    } else {
      if (source) {
        source.stop(0);
        setSource(null);
      }
      if (recognizer) {
        recognizer.recognized = null;
        recognizer.close();
        setRecognizer(null);
      }
      if (synthesizer) {
        synthesizer.close();
        setSynthesizer(null);
      }
    }
  }, [assistant.value]);

  useEffect(() => {
    if (render) {
      setStreaming(true);
      synthesizer.speakTextAsync(
        I18n.t("MSG_ASSISTANT_WELCOME_MESSAGE"),
        async (result) => {
          const { audioData } = result;
          handleAudioPlay(audioData, recognizer);
        }
      );
    }
  }, [render]);

  const handlePause = () => {
    play();
    if (source) {
      source.stop(0);
    }
    setStreaming(false);
  };

  const handleClose = () => {
    play();
    handleDialog({ type: DialogType.ASSISTANT, value: false });
  };

  return render ? (
    ReactDOM.createPortal(
      <div
        onAnimationEnd={animationEnd}
        className="flex-col-el items-end absolute right-[10px] bottom-[100px] z-[500000] animate__animated animate__bounceInUp"
      >
        <div className="flex-col-center bg-white/90 rounded-xl p-4  mr-[130px]">
          <div className="flex-col-el absolute top-0 right-0 scale-75">
            <CloseBtn close={handleClose} />
          </div>
          <div className="flex-col-el w-[500px] max-w-[500px] min-w-[100px]">
            <p className="w-full text-body text-textcolor/black text-left text-[16px] break-all">
              {text}
            </p>
          </div>

          <div
            onClick={handlePause}
            className="flex-col-center pt-4 cursor-pointer active:opacity-70"
          >
            <StopCircleIcon sx={{ fontSize: 60, color: "gray" }} />
          </div>
        </div>

        <div className="flex-col-el w-[36px] h-[36px] mr-[150px]">
          <div
            className="flex-col-el relative w-0 h-0 border-solid border-x-transparent border-t-white/90"
            style={{
              borderWidth: "36px 20px 0 20px",
            }}
          />
        </div>

        <img
          alt="robot assistant"
          src={`${Constants.assetHost}/assets/images/robot_assistant.png`}
          style={{ width: 200 * scale, height: 200 * scale }}
        />
      </div>,
      document.body
    )
  ) : (
    <></>
  );
};
